'use client'
import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { UserInfo, UpdateUserInfo } from '@/types/user';
import { getUserInfo, updateUserInfo } from '@/services/user';
import { useError } from '@/contexts/ErrorContext';
import { UserContextType, UserProviderProps } from '@/types/contexts';
import { isRedirectingToLogin, redirectToLogin } from '@/services/api';
import { areAllTokensInvalid } from '@/services/token';

const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const { handleError } = useError();

  const refreshUserInfo = useCallback(async () => {
    try {
      const response = await getUserInfo();
      if (response.success && response.data) {
        setUser(prev => {
          if (prev && JSON.stringify(prev) === JSON.stringify(response.data)) {
            return prev;
          }
          return response.data!;
        });
      } else {
        // トークンが全て無効な場合はログイン画面へリダイレクト
        if (areAllTokensInvalid()) {
          redirectToLogin();
        } else if (!isRedirectingToLogin()) {
          handleError('UPDATE_USER_ERROR');
        }
        setUser(prev => prev === null ? prev : null);
      }
    } catch {
      // トークンが全て無効な場合はログイン画面へリダイレクト
      if (areAllTokensInvalid()) {
        redirectToLogin();
      } else if (!isRedirectingToLogin()) {
        handleError('UPDATE_USER_ERROR');
      }
      setUser(prev => prev === null ? prev : null);
    }
  }, [handleError]);

  const updateUser = useCallback(async (data: UpdateUserInfo) => {
    try {
      const response = await updateUserInfo(data);
      if (response.success) {
        await refreshUserInfo();
      } else {
        handleError('UPDATE_USER_ERROR');
        throw new Error(response.message);
      }
    } catch (error) {
      handleError('UPDATE_USER_ERROR');
      throw error;
    }
  }, [refreshUserInfo, handleError]);

  const clearUser = useCallback(() => {
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    updateUser,
    refreshUserInfo,
    clearUser,
  }), [user, updateUser, refreshUserInfo, clearUser]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
